import React from 'react';
import type { ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import type { OptionData, PrintOrientationTypeEnum } from 'venn-api';
import { SCALE_FACTOR } from 'venn-state';
import VerticalHeader from './VerticalHeader';
import { pairwiseCorrelationCellRenderer } from './cellRenderers';
import type { TabularDataRow } from './types';
import { OptionIcon } from '../../../../common/OptionIcon';
import { FIRST_COLUMN_INNER_WIDTH } from '../../../../blocks/components/visualizations/grids/correlation-grid';

export const LABEL_COLUMN_WIDTH_PX = FIRST_COLUMN_INNER_WIDTH + 10;
export const HEADER_HEIGHT_PX = 160 * SCALE_FACTOR;
export const DATA_COLUMN_MIN_WIDTH_PX = 35;
export const REPORT_LAB_DATA_COLUMN_MIN_WIDTH_PX = 25;

/**
 * A landscape page is shorter than a portrait one, so rows are still shrunk relative to portrait to
 * fit more of the (up to MAX_INVESTMENTS) rows on one page — but AG-Grid enforces `rowHeight`
 * literally (unlike the old HTML table's `<tr>`, which could stay readable even when tight), so the
 * multiplier needs more headroom than the old table used.
 */
export const ROW_HEIGHT_FONT_MULTIPLIER: Record<PrintOrientationTypeEnum, number> = {
  PORTRAIT: 2.7,
  LANDSCAPE: 2,
};

export const LABEL_CELL_TOP_PADDING_PX: Record<PrintOrientationTypeEnum, number> = {
  PORTRAIT: 5,
  LANDSCAPE: 3,
};

interface CorrelationLabelHeaderCellProps extends IHeaderParams {
  fontSize: string;
}

export const CorrelationLabelHeaderCell = ({ fontSize }: CorrelationLabelHeaderCellProps) => (
  <div className="flex h-full w-full items-end pl-0 pb-[3px]" style={{ fontSize }}>
    Correlation
  </div>
);

interface CorrelationColumnHeaderCellProps extends IHeaderParams {
  columnLabel: string;
  optionData?: OptionData;
}

export const CorrelationColumnHeaderCell = ({ columnLabel, optionData }: CorrelationColumnHeaderCellProps) => {
  const icon = optionData ? <OptionIcon optionType={optionData.optionType} /> : null;
  return (
    <VerticalHeader>
      {icon} {columnLabel}
    </VerticalHeader>
  );
};

interface CorrelationValueCellProps extends ICellRendererParams<TabularDataRow> {
  colNum: number;
}

export const CorrelationValueCell = ({ data, node, colNum }: CorrelationValueCellProps) => {
  if (!data) return null;
  return pairwiseCorrelationCellRenderer(colNum, 0, undefined)(data, node.rowIndex ?? 0);
};
